import React, { useContext, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Divider,
  Portal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

import {
  RaisedHandIcon,
  QueueIcon,
  RecordIcon,
  CameraIcon,
  MicrophoneIcon,
  ShareIcon,
  ConfigIcon,
  LeaveIcon,
  FollowIcon,
  SubscribeIcon,
  ClipIcon,
} from "../icons/stream";
import "./StreamingControls.scss";

import { useTranslation } from "react-i18next";
import { SocketContext } from "../../context/socket";
import SongRequestButton from "./SongRequestButton";
import SongRequestList from "./SongRequestList";
import cookieStorage from "../../services/cookie-storage";
import ApiService from "../../services/api-service";
import { copyShareUrl } from "../../constants/function";
import notifier from "../Notifier";
import emitter from "../../EventEmitter";
import { getBalance } from "../../asyncAction/asyncBalance";
import ClipEditor from "./ClipEditor";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import GiftSubscription from "../GiftSubscription";
import { fullName } from "../../constants/function";

export default function StreamingControls({
  role,
  isOwner,
  isModerator = false,
  isObsAdmin = false,
  isMute,
  video,
  mute,
  disableVideo,
  isVideo,
  isScreenSharing,
  shareScreen,
  stopShare,
  userId,
  playlistId,
  tokenData,
  liveUsers,
  streamData,
  obs,
  isJoined,
  raisedList = [],
  mics = [],
  cameras = [],
  currentMic,
  currentCamera,
  switchAudio,
  switchVideo,
  showRecordButton,
  onTipHost,
  onFollowHost,
  onSubscribeHost,
  onAcceptSongRequest,
  hostId,
  channelName,
  livestreamId,
  songData,
  songTimelineRef,
  hlsUrl,
}) {
  const { t } = useTranslation("common");
  const socket = useContext(SocketContext).Socket;

  const [openSongRequests, setOpenSongRequests] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openSetting = Boolean(anchorEl);

  const [record, setRecord] = useState(false);
  const [starting, setStarting] = useState(false);
  const [open, setOpen] = useState(false);
  const [newRaised, setNewRaise] = useState(false);

  const [availableMics, setAvailableMics] = useState([]);
  const [availableCameras, setAvailableCameras] = useState([]);

  const mediaRecorderRef = React.useRef(null);
  const recordedChunksRef = React.useRef([]);
  const screenStreamRef = React.useRef(null);
  const recordingStartTimeRef = React.useRef(null);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [isUploading, setIsUploading] = React.useState(false);

  const [openAudienceSongRequest, setOpenAudienceSongRequest] = useState(false);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [openClipEditor, setOpenClipEditor] = useState(false);
  const [openGift, setOpenGift] = useState(false);
  const [hostSellsSubs, setHostSellsSubs] = useState(false);
  const [hostMonthlyFee, setHostMonthlyFee] = useState(0);
  const [hostName, setHostName] = useState("");

  // Follow / Subscribe state
  const [isFollowing, setIsFollowing] = useState(false);
  const [isSubscribedToHost, setIsSubscribedToHost] = useState(false);
  const [subscribedEndDate, setSubscribedEndDate] = useState(null);

  // Subscribe confirmation dialog state
  const [openSubscribeDialog, setOpenSubscribeDialog] = useState(false);
  const [subscribeDialogMode, setSubscribeDialogMode] = useState('subscribe'); // 'subscribe' | 'unsubscribe'
  const [subscriptionFee, setSubscriptionFee] = useState(null);
  const [loadingSubscribeInfo, setLoadingSubscribeInfo] = useState(false);
  const dispatch = useDispatch();
  const { bal } = useSelector((state) => state.balance);
  const displayBal = bal > 0 ? bal : parseFloat(localStorage.getItem("balance") || "0");

  // Fetch follow/subscribe status on mount
  useEffect(() => {
    if (!hostId || isOwner) return;
    const fetchStatus = async () => {
      try {
        const response = await ApiService.USER_DETAILS(hostId);
        const user = response?.result;
        if (user) {
          setIsFollowing(!!user.isFollowed);
          setIsSubscribedToHost(!!user.isSubscribed);
          // A gift is a subscription, so it needs the same two things
          // Subscribe needs: the host sells one, at a price above zero.
          setHostSellsSubs(
            user.subscriptionStatus === "enabled" && Number(user.subscriptionFee) > 0
          );
          setHostMonthlyFee(Number(user.subscriptionFee) || 0);
          setHostName(fullName(user));
          if (user.subscribedEndDate) setSubscribedEndDate(new Date(user.subscribedEndDate));
        }
      } catch (error) {
        console.error('[StreamingControls] Failed to fetch host status:', error);
      }
    };
    fetchStatus();
  }, [hostId, isOwner]);

  const handleOpenSubscribeDialog = async () => {
    const mode = isSubscribedToHost ? 'unsubscribe' : 'subscribe';
    setSubscribeDialogMode(mode);
    setOpenSubscribeDialog(true);
    setLoadingSubscribeInfo(true);
    dispatch(getBalance());
    try {
      const response = await ApiService.USER_DETAILS(hostId);
      const user = response?.result;
      const fee = user?.subscriptionFee;
      setSubscriptionFee(fee != null ? Number(fee) : null);
      if (user?.subscribedEndDate) setSubscribedEndDate(new Date(user.subscribedEndDate));
    } catch (error) {
      console.error('[StreamingControls] Failed to fetch subscription info:', error);
      setSubscriptionFee(null);
    } finally {
      setLoadingSubscribeInfo(false);
    }
  };

  const handleConfirmSubscribe = async () => {
    setOpenSubscribeDialog(false);
    if (subscribeDialogMode === 'unsubscribe') {
      try {
        await ApiService.UNSUBSCRIBE_ARTIST({ artistId: hostId, currency: 'siki' });
        setIsSubscribedToHost(false);
        setSubscribedEndDate(null);
        notifier.success('Unsubscribed from host');
      } catch (err) {
        notifier.error('Failed to unsubscribe');
      }
    } else if (onSubscribeHost) {
      try {
        await onSubscribeHost();
        setIsSubscribedToHost(true);
        setSubscribedEndDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
      } catch (err) {
        // error handled in parent
      }
    }
  };

  const formatDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const now = new Date();
    const diff = endDate.getTime() - now.getTime();
    if (diff <= 0) return 'Expired';
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days === 1 ? '1 day' : `${days} days`;
  };

  // The jukebox is shared by the host and their moderators.
  const runsJukebox = isOwner || isModerator;

  // Fetch pending song request count for whoever runs the jukebox
  useEffect(() => {
    if (!runsJukebox || !playlistId) return;

    const fetchPendingCount = async () => {
      try {
        const response = await ApiService.SONG_REQUEST_LIST(playlistId);
        if (response?.success) {
          const count = response.result?.length || 0;
          setPendingRequestCount(count);
        }
      } catch (error) {
        console.error('[StreamingControls] Error fetching pending requests:', error);
      }
    };

    // Fetch initially
    fetchPendingCount();

    // Refresh when song requests dialog closes
    if (!openSongRequests) {
      fetchPendingCount();
    }
  }, [runsJukebox, playlistId, openSongRequests]);

  const handleShareStream = async () => {
    try {
      await copyShareUrl('livestream', playlistId);
      notifier.success('Link copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      notifier.error('Failed to copy link');
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (record && mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();

        if (screenStreamRef.current) {
          screenStreamRef.current.getTracks().forEach(track => track.stop());
        }

        e.preventDefault();
        e.returnValue = 'Recording in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [record]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioDevices = devices.filter((device) => device.kind === "audioinput");
        const videoDevices = devices.filter((device) => device.kind === "videoinput");

        setAvailableMics(audioDevices.map((device) => ({ label: device.label, deviceId: device.deviceId })));
        setAvailableCameras(videoDevices.map((device) => ({ label: device.label, deviceId: device.deviceId })));
      } catch (error) {
        console.error("Error fetching devices:", error);
      }
    };

    fetchDevices();

    navigator.mediaDevices.addEventListener("devicechange", fetchDevices);
    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", fetchDevices);
    };
  }, []);

  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  
  const handleClose = () => {
    setAnchorEl(null);
  };

  
  const handleSwitchAudio = (micLabel) => {
    console.log(`[StreamingControls] Switching audio to: ${micLabel}`);
    switchAudio(micLabel);
    handleClose();
  };

  
  const handleSwitchVideo = (cameraLabel) => {
    console.log(`[StreamingControls] Switching video to: ${cameraLabel}`);
    switchVideo(cameraLabel);
    handleClose();
  };


const handleRecord = async () => {
  if (!record) {
    setStarting(true);
    try {
      console.log("[StreamingControls] Starting screen recording...");

      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 }
        },
        audio: true,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
        systemAudio: 'include'
      });

      let micStream = null;
      try {
        micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      } catch (micError) {
        console.warn("[StreamingControls] Could not get microphone access:", micError);
      }

      const audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();

      const screenAudioTracks = screenStream.getAudioTracks();
      if (screenAudioTracks.length > 0) {
        const screenAudioSource = audioContext.createMediaStreamSource(new MediaStream(screenAudioTracks));
        const screenGain = audioContext.createGain();
        screenGain.gain.setValueAtTime(0.7, audioContext.currentTime);
        screenAudioSource.connect(screenGain);
        screenGain.connect(destination);
      }

      if (micStream) {
        const micAudioSource = audioContext.createMediaStreamSource(micStream);
        const micGain = audioContext.createGain();
        micGain.gain.setValueAtTime(0.9, audioContext.currentTime);
        micAudioSource.connect(micGain);
        micGain.connect(destination);
      }

      const combinedStream = new MediaStream([
        ...screenStream.getVideoTracks(),
        ...destination.stream.getAudioTracks()
      ]);

      screenStreamRef.current = screenStream;
      recordedChunksRef.current = [];

      const micStreamRef = micStream;
      const audioContextRef = audioContext;

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
        ? 'video/webm;codecs=vp8'
        : 'video/webm';

      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType,
        videoBitsPerSecond: 5000000
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      const recordedMimeType = mimeType;
      mediaRecorder.onstop = async () => {
        console.log("[StreamingControls] Recording stopped, uploading to server...");

        const blob = new Blob(recordedChunksRef.current, { type: recordedMimeType });

        const duration = recordingStartTimeRef.current
          ? (Date.now() - recordingStartTimeRef.current) / 1000
          : 0;

        try {
          setIsUploading(true);
          setUploadProgress(0);

          const formData = new FormData();
          formData.append('recording', blob, `screen-recording-${Date.now()}.webm`);
          formData.append('livestreamId', playlistId);
          formData.append('title', `Screen Recording - ${new Date().toLocaleString()}`);
          formData.append('duration', duration.toString());

          const response = await ApiService.uploadRecording(formData, (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
            console.log(`[StreamingControls] Upload progress: ${progress}%`);
          });

          console.log("[StreamingControls] Recording uploaded successfully:", response);
          notifier.success('Recording saved to your livestreams!');

        } catch (error) {
          console.error("[StreamingControls] Failed to upload recording:", error);
          notifier.error('Failed to upload recording. Downloading locally instead.');

          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          a.download = `livestream-recording-${timestamp}.webm`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } finally {
          setIsUploading(false);
          setUploadProgress(0);
        }

        recordedChunksRef.current = [];
        recordingStartTimeRef.current = null;

        if (micStreamRef) {
          micStreamRef.getTracks().forEach(track => track.stop());
        }
        if (audioContextRef) {
          audioContextRef.close();
        }
      };

      screenStream.getVideoTracks()[0].onended = () => {
        if (record) {
          console.log("[StreamingControls] Screen share ended by user");
          if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
          }
          if (micStreamRef) {
            micStreamRef.getTracks().forEach(track => track.stop());
          }
          if (audioContextRef) {
            audioContextRef.close();
          }
          setRecord(false);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      recordingStartTimeRef.current = Date.now();
      mediaRecorder.start(1000);

      setRecord(true);
      setStarting(false);
      console.log("[StreamingControls] Screen recording started successfully");

    } catch (error) {
      console.error("[StreamingControls] Failed to start screen recording:", error);
      setStarting(false);

      if (error.name === 'NotAllowedError') {
        notifier.error('Screen recording permission denied. Please allow screen sharing to record.');
      } else if (error.name === 'NotSupportedError') {
        notifier.error('Screen recording is not supported in this browser.');
      }
    }
  } else {
    console.log("[StreamingControls] Stopping screen recording...");
    setStarting(true);

    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }

      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
        screenStreamRef.current = null;
      }

      setRecord(false);
      setStarting(false);
    } catch (error) {
      console.error("[StreamingControls] Error stopping recording:", error);
      setStarting(false);
    }
  }
};

  
  // Gifting a subscription to someone watching, the way it works on Twitch or
  // Kick. The recipients are the people in this room; the artist is the host.
  const renderGift = () => {
    if (!hostSellsSubs || !hostId || isOwner) return null;
    return (
      <>
        <IconButton
          className="stream-control-btn"
          onClick={() => setOpenGift(true)}
          title="Gift a subscription"
          aria-label="Gift a subscription to someone watching"
        >
          <CardGiftcardIcon sx={{ fontSize: 18 }} />
        </IconButton>
        {openGift && (
          <GiftSubscription
            open={openGift}
            onClose={() => setOpenGift(false)}
            artistId={hostId}
            artistName={hostName || "this artist"}
            monthlyFee={hostMonthlyFee}
            candidates={liveUsers}
          />
        )}
      </>
    );
  };

  // The queue a host or moderator works from: approve or reject what the
  // audience has asked for. A moderator renders in the audience row, so this
  // lives on its own rather than inside the host row, which they never reach.
  const renderJukeboxQueue = () => {
    if (!(isJoined || obs) || !runsJukebox) return null;
    return (
      <>
        <Badge
          badgeContent={pendingRequestCount}
          color="error"
          variant="dot"
          invisible={pendingRequestCount === 0}
          className="stream-control-badge"
        >
          <IconButton
            className="stream-control-btn"
            onClick={() => setOpenSongRequests(true)}
            title="Song Requests"
          >
            <QueueIcon />
          </IconButton>
        </Badge>
        {openSongRequests && (
          <SongRequestList
            open={openSongRequests}
            onClose={() => setOpenSongRequests(false)}
            roomId={playlistId}
            onAcceptSong={onAcceptSongRequest}
          />
        )}
      </>
    );
  };

  const renderHostOrCohostControls = () => (
    <>
      {/* Raised Hands */}
      {raisedList.length > 0 && (
        <button
          className="stream-control-btn stream-control-btn--raised"
          onClick={() => {
            setOpen(!open);
            setNewRaise(false);
          }}
        >
          <RaisedHandIcon active size={16} />
          <span className="raised-text">{t("stream.raisedHands")}</span>
          {newRaised && <span className="raised-dot" />}
        </button>
      )}

      {renderJukeboxQueue()}
      {renderGift()}

      <div className="stream-controls-divider" />

      {/* Record */}
      {!obs && showRecordButton ? (
        starting ? (
          <IconButton className="stream-control-btn stream-control-btn--record">
            <CircularProgress
              sx={{ width: "22px !important", height: "22px !important" }}
              style={{ color: 'rgba(255,58,58,0.7)' }}
            />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleRecord}
            className={`stream-control-btn stream-control-btn--record${record ? ' stream-control-btn--recording' : ''}`}
            disabled={starting}
            title={record ? t("stream.tooltip4") : t("stream.tooltip5")}
          >
            {record && <div className="stream-live-dot" />}
            <RecordIcon active={record} />
          </IconButton>
        )
      ) : null}

      {/* Camera — hidden for OBS admin (OBS is the video source) */}
      {isJoined && !isObsAdmin && (
        <IconButton
          title={video ? t("stream.tooltip8") : t("stream.tooltip9")}
          onClick={disableVideo}
          className="stream-control-btn"
          disabled={video === null}
        >
          <CameraIcon off={!video} />
        </IconButton>
      )}

      {/* Mic — hidden for OBS admin (OBS is the audio source) */}
      {isJoined && !isObsAdmin && (
        <IconButton
          title={isMute ? t("stream.tooltip10") : t("stream.tooltip11")}
          onClick={mute}
          className="stream-control-btn"
        >
          <MicrophoneIcon off={isMute} />
        </IconButton>
      )}

      <div className="stream-controls-divider" />

      {/* Share */}
      <IconButton
        className="stream-control-btn"
        onClick={handleShareStream}
        title="Share Stream"
      >
        <ShareIcon />
      </IconButton>

      {/* Settings / Config */}
      {isJoined && (
        <div style={{ position: 'relative' }}>
          <IconButton
            className={`stream-control-btn${anchorEl ? ' stream-control-btn--active' : ''}`}
            onClick={handleClick}
            title={t("stream.tooltip12")}
          >
            <ConfigIcon active={Boolean(anchorEl)} />
          </IconButton>
          <Portal>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              transformOrigin={{ vertical: "bottom", horizontal: "center" }}
              slotProps={{
                paper: {
                  style: {
                    backgroundColor: '#2A2A2A',
                    color: '#ffffff',
                    border: '1px solid #444',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    minWidth: 200,
                    zIndex: 99999,
                  },
                }
              }}
              sx={{
                zIndex: 99999,
                '& .MuiMenuItem-root': {
                  color: '#ffffff',
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: '#444',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#555',
                  },
                },
              }}
            >
              <MenuItem onClick={handleClose}>Test Menu Item</MenuItem>
              {availableMics.length > 0 ? (
                availableMics.map((mic, index) => (
                  <MenuItem
                    key={index}
                    selected={mic.label === currentMic?.label}
                    onClick={() => handleSwitchAudio(mic.label)}
                  >
                    {mic.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No microphones available</MenuItem>
              )}
              <Divider sx={{ my: 0.5, borderColor: '#666' }} />
              {availableCameras.length > 0 ? (
                availableCameras.map((camera, index) => (
                  <MenuItem
                    key={index}
                    selected={camera.label === currentCamera?.label}
                    onClick={() => handleSwitchVideo(camera.label)}
                  >
                    {camera.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No cameras available</MenuItem>
              )}
            </Menu>
          </Portal>
        </div>
      )}
    </>
  );

  if (role === "host" || role === "cohost") {
    return (
      <div className="stream-controls--host">
        <div className="stream-controls-toolbar">
          {renderHostOrCohostControls()}
          <IconButton
            className="stream-control-btn"
            onClick={() => setOpenClipEditor(true)}
            title="Clip"
          >
            <ClipIcon />
          </IconButton>
          {role === 'cohost' && (
            <>
              <div className="stream-controls-divider" />
              <IconButton
                className="stream-control-btn"
                title="Leave co-host"
                onClick={() => {
                  const jwt = cookieStorage.getCookie('Authorization');
                  if (!socket) return;
                  if (socket.timeout) {
                    socket.timeout(5000).emit('cohost-leave', { JWT: jwt, channelName: playlistId }, (err, res) => {
                      if (err) {
                        console.error('Failed to leave co-host');
                        return;
                      }
                      const payload = Array.isArray(res) && res.length ? res[res.length - 1] : res;
                      if (payload && payload.status === 'ok') {
                        console.log('Left co-host successfully');
                      } else if (payload && payload.status === 'is_owner') {
                        console.error('Host cannot leave co-host role');
                      } else {
                        console.error('Failed to leave co-host');
                      }
                    });
                  } else {
                    socket.emit('cohost-leave', { JWT: jwt, channelName: playlistId });
                  }
                }}
              >
                <LeaveIcon />
              </IconButton>
            </>
          )}
        </div>
        {openClipEditor && (
          <ClipEditor
            open={openClipEditor}
            onClose={() => setOpenClipEditor(false)}
            channelName={channelName}
            livestreamId={livestreamId}
            songData={songData}
            songTimelineRef={songTimelineRef}
            hlsUrl={hlsUrl}
          />
        )}
      </div>
    );
  }

  return (
    <div className="stream-controls--audience">
      <div className="stream-controls-toolbar">
        {/* Request Song / Jukebox. A moderator runs the queue instead of
            asking to join it, so they get the host's approve-or-reject list. */}
        {runsJukebox ? (
          renderJukeboxQueue()
        ) : (
          <button
            onClick={() => setOpenAudienceSongRequest(true)}
            className="stream-action-btn stream-action-btn--subscribe"
            title="Request Song"
          >
            <QueueIcon size={14} />
            <span className="stream-action-text">Jukebox</span>
          </button>
        )}

        {renderGift()}

        {/* Clip */}
        <IconButton
          className="stream-control-btn"
          onClick={() => setOpenClipEditor(true)}
          title="Clip"
        >
          <ClipIcon />
        </IconButton>

        {/* Share */}
        <IconButton
          className="stream-control-btn"
          onClick={handleShareStream}
          title="Share Stream"
        >
          <ShareIcon />
        </IconButton>

        <div className="stream-controls-divider" />

        {/* Follow */}
        {!(isOwner || (userId && hostId && userId.toString() === hostId.toString())) && (
          <button
            onClick={async () => {
              if (onFollowHost) {
                const newState = await onFollowHost(isFollowing);
                setIsFollowing(newState);
              }
            }}
            className={`stream-action-btn stream-action-btn--follow${isFollowing ? ' stream-action-btn--active-state' : ''}`}
            title={isFollowing ? "Unfollow" : "Follow"}
          >
            <FollowIcon size={14} />
            <span className="stream-action-text">{isFollowing ? "Following" : "Follow"}</span>
          </button>
        )}

        {/* Subscribe */}
        {!(isOwner || (userId && hostId && userId.toString() === hostId.toString())) && (
          <button
            onClick={() => handleOpenSubscribeDialog()}
            className={`stream-action-btn stream-action-btn--subscribe${isSubscribedToHost ? ' stream-action-btn--active-state' : ''}`}
            title={isSubscribedToHost ? "Subscribed" : "Subscribe"}
          >
            <SubscribeIcon size={14} />
            <span className="stream-action-text">{isSubscribedToHost ? "Subscribed" : "Subscribe"}</span>
          </button>
        )}
      </div>

      {/* Song Request Dialog */}
      {openAudienceSongRequest && (
        <SongRequestButton
          hostId={hostId}
          roomId={playlistId}
          isOwner={false}
          open={openAudienceSongRequest}
          onClose={() => setOpenAudienceSongRequest(false)}
        />
      )}

      {/* Clip Editor */}
      {openClipEditor && (
        <ClipEditor
          open={openClipEditor}
          onClose={() => setOpenClipEditor(false)}
          channelName={channelName}
          livestreamId={livestreamId}
          songData={songData}
          songTimelineRef={songTimelineRef}
          hlsUrl={hlsUrl}
        />
      )}

      {/* Subscribe Confirmation Dialog */}
      <Dialog
        open={openSubscribeDialog}
        onClose={() => setOpenSubscribeDialog(false)}
        PaperProps={{
          style: {
            backgroundColor: '#1A1A1A',
            color: '#fff',
            border: '1px solid rgba(255,215,0,0.2)',
            borderRadius: '14px',
            minWidth: 320,
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: "var(--font-display)",
          fontSize: '14px',
          letterSpacing: '2px',
          color: subscribeDialogMode === 'unsubscribe' ? 'rgba(255,100,100,0.9)' : 'rgba(255,215,0,0.9)',
          textTransform: 'uppercase',
          textAlign: 'center',
          pb: 1,
        }}>
          {subscribeDialogMode === 'unsubscribe' ? 'Unsubscribe' : 'Subscribe'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {loadingSubscribeInfo ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <CircularProgress size={28} style={{ color: 'rgba(255,215,0,0.6)' }} />
            </div>
          ) : subscribeDialogMode === 'unsubscribe' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Remaining time */}
              <div style={{
                background: '#2A2A2A',
                borderRadius: '10px',
                padding: '14px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                  Time Remaining
                </span>
                <span style={{
                  color: 'rgba(255,215,0,0.9)',
                  fontWeight: 700,
                  fontSize: '16px',
                }}>
                  {formatDaysRemaining(subscribedEndDate) || 'N/A'}
                </span>
              </div>

              {/* Warning */}
              <div style={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '12px',
                textAlign: 'center',
                lineHeight: '1.5',
                padding: '4px 0',
              }}>
                Are you sure you want to unsubscribe? You still have{' '}
                <span style={{ color: 'rgba(255,215,0,0.9)', fontWeight: 600 }}>
                  {formatDaysRemaining(subscribedEndDate) || '0 days'}
                </span>{' '}
                left on your subscription.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Subscription Fee */}
              <div style={{
                background: '#2A2A2A',
                borderRadius: '10px',
                padding: '14px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                  Subscription Fee
                </span>
                <span style={{
                  color: 'rgba(255,215,0,0.9)',
                  fontWeight: 700,
                  fontSize: '16px',
                }}>
                  {subscriptionFee != null ? `${subscriptionFee} SIKI` : 'N/A'}
                </span>
              </div>

              {/* Your Balance */}
              <div style={{
                background: '#2A2A2A',
                borderRadius: '10px',
                padding: '14px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                  Your Balance
                </span>
                <span style={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '16px',
                }}>
                  {displayBal.toFixed(2)} SIKI
                </span>
              </div>

              {/* Insufficient balance warning */}
              {subscriptionFee != null && displayBal < subscriptionFee && (
                <div style={{
                  color: '#ff3a3a',
                  fontSize: '12px',
                  textAlign: 'center',
                  padding: '4px 0',
                }}>
                  Insufficient balance
                </div>
              )}

              {/* Duration info */}
              <div style={{
                color: 'rgba(255,255,255,0.35)',
                fontSize: '11px',
                textAlign: 'center',
              }}>
                Subscription lasts 30 days
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: '10px', pb: 2.5, px: 3 }}>
          <Button
            onClick={() => setOpenSubscribeDialog(false)}
            sx={{
              color: 'rgba(255,255,255,0.5)',
              borderColor: 'rgba(255,255,255,0.15)',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '13px',
              px: 3,
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.3)',
                background: 'rgba(255,255,255,0.04)',
              },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          {subscribeDialogMode === 'unsubscribe' ? (
            <Button
              onClick={handleConfirmSubscribe}
              disabled={loadingSubscribeInfo}
              sx={{
                background: 'rgba(255,100,100,0.12)',
                color: 'rgba(255,100,100,0.9)',
                border: '1px solid rgba(255,100,100,0.35)',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  background: 'rgba(255,100,100,0.2)',
                  borderColor: 'rgba(255,100,100,0.6)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255,255,255,0.2)',
                  borderColor: 'rgba(255,255,255,0.1)',
                  background: 'rgba(255,255,255,0.04)',
                },
              }}
            >
              Unsubscribe
            </Button>
          ) : (
            <Button
              onClick={handleConfirmSubscribe}
              disabled={loadingSubscribeInfo || subscriptionFee == null || displayBal < subscriptionFee}
              sx={{
                background: 'rgba(255,215,0,0.12)',
                color: 'rgba(255,215,0,0.9)',
                border: '1px solid rgba(255,215,0,0.35)',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  background: 'rgba(255,215,0,0.2)',
                  borderColor: 'rgba(255,215,0,0.6)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255,255,255,0.2)',
                  borderColor: 'rgba(255,255,255,0.1)',
                  background: 'rgba(255,255,255,0.04)',
                },
              }}
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}
